import { ChangeDetectionStrategy, Component, OnInit, computed, effect, inject, input, model, output } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { AppConfig } from 'src/app/app-config.loader';
import { AnagraficaClassificheService } from 'src/app/core/anagrafica-classifiche.service';
import { PosizioniService } from 'src/app/core/posizioni.service';
import { GestionePosizioniItem } from 'src/app/features/gestione-posizioni/gestione-posizioni.model';
import { Classifica } from '../../models/classifica.model';
import { POSIZIONE_ROOT, Posizione } from '../../models/posizione.model';

export type PosizioniEventType = 'add' | 'mod';

@Component({
  selector: 'app-add-position',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './add-position.component.html',
  styleUrls: ['./add-position.component.scss']
})
export class AddPositionComponent implements OnInit {
  visible = model.required<boolean>();
  typeOfEvent = input.required<PosizioniEventType>();
  nodeToModify = input<GestionePosizioniItem | null>(null);

  currentFather = computed(() => this.nodeToModify()?.posizioneId ?? POSIZIONE_ROOT);
  currentLevel = computed(() => this.nodeToModify()?.level ?? 0);
  targetLevel = computed(() => {
    const nodeToModify = this.nodeToModify();
    if (this.isModify()) {
      return nodeToModify?.level ?? 0;
    }

    return nodeToModify ? nodeToModify.level + 1 : 0;
  });
  isFascicolo = computed(() => this.targetLevel() === this.fascicoloLevel);

  isCompleted = output<Posizione>();
  closed = output();

  public posizioneRoot = POSIZIONE_ROOT;

  private appConfig = inject(AppConfig);
  private classificheService = inject(AnagraficaClassificheService);
  private posizioniService = inject(PosizioniService);
  private messageService = inject(MessageService);

  classifiche: Classifica[] = [];
  tree: Posizione[] = [];
  folderForm = new FormGroup({
    // eslint-disable-next-line @typescript-eslint/unbound-method
    name: new FormControl<string>('', { validators: [Validators.required, this.validateSiglaNotExisting()], nonNullable: true }),
    classificaId: new FormControl<number | null>(null),
    descrizione: new FormControl<string>(''),
    // eslint-disable-next-line @typescript-eslint/unbound-method
    dataApertura: new FormControl<Date>(new Date(), { validators: [Validators.required], nonNullable: true }),
    dataChiusura: new FormControl<Date | null>(null),
    dataScadenza: new FormControl<Date | null>(null),
    paroleChiave: new FormControl<string[]>([], { validators: [this.validateParoleChiave()], nonNullable: true }),
    note: new FormControl<string>(''),
    posizioneFisica: new FormControl<string>(''),
    progressivo: new FormControl<number | null>(null, { validators: [Validators.min(1)] }),
    tipologiaFascicolo: new FormControl<string | null>(null),
  });

  today = new Date();
  fascicoloLevel = this.appConfig.logic.hierarchy.descriptionIndex;
  levelLabels = this.appConfig.logic.hierarchy.labels.map(l => l.toLowerCase());
  tipologiaFascicoloOptions = [
    { label: 'Affare', value: 'affare' },
    { label: 'Attivita', value: 'attivita' },
    { label: 'Persona fisica', value: 'persona fisica' },
    { label: 'Persona giuridica', value: 'persona giuridica' },
    { label: 'Procedimento amministrativo', value: 'procedimento amministrativo' },
  ];
  expandSearchedRows = false;
  isMoveDialogActive = false;

  public disableFromLevel = -1;
  public nodeFrom: string | null = null;

  constructor() {
    // Syncing the reactive form when the dialog opens drives PrimeNG controls,
    // which update internal signals during writeValue/reset.
    effect(() => {
      if (!this.visible()) {
        return;
      }

      const nodeToModify = this.nodeToModify();
      if (nodeToModify && this.isModify()) {
        this.populateForm(nodeToModify);
        return;
      }

      this.resetForm();
    }, { allowSignalWrites: true });
  }

  ngOnInit(): void {
    this.classificheService.getAllArray(classifiche => {
      this.classifiche = classifiche;
    });

    this.posizioniService.getTree().subscribe(
      (res) => {
        this.tree = [...res];
      }
    );

    setTimeout(() => document.getElementById('name')?.focus(), 20);
  }

  closeModalAddFolder(): void {
    this.resetForm();
    this.closed.emit();
  }

  addFolder(): void {
    const nodeToModify = this.nodeToModify();
    if (nodeToModify) {
      !this.isModify() ? this.prepareToAddInFolder(nodeToModify) : this.prepareToModify(nodeToModify);
    } else {
      this.prepareNewToSend();
    }
  }

  public validateSiglaNotExisting(): ValidatorFn {
    return (control: AbstractControl<string>): ValidationErrors | null => {
      if (this.controlDoubleInList1(control.value, this.currentFather())) {
        return { existing: true };
      }
      return null;
    };
  }

  public validateParoleChiave(): ValidatorFn {
    return (control: AbstractControl<string[]>): ValidationErrors | null => {
      const values = (control.value ?? [])
        .map(value => value?.trim())
        .filter((value): value is string => !!value);

      if (values.length > 5) {
        return { maxItems: true };
      }

      if (values.some(value => value.length > 255)) {
        return { maxLength: true };
      }

      return null;
    };
  }

  private controlDoubleInList1(sigla: string, position: string): Posizione | undefined {
    sigla = sigla.toLowerCase();
    if (position === POSIZIONE_ROOT) {
      return this.tree.find((node: Posizione) => {
        return node.posizioneId.split('/').length === 3 && sigla === node.sigla.toLowerCase();
      });
    }

    const pattern = new RegExp(`^${position.replace(/\//g, '\\/')}\\d+\\/$`, 'g');
    return this.tree.find((node: Posizione) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      return (pattern.exec(node.posizioneId)) && sigla === node.sigla.toLowerCase();
    });
  }

  private controlDoubleInList(position: string): Posizione | undefined {
    const sigla = this.folderForm.getRawValue().name.toLowerCase();
    if (position === POSIZIONE_ROOT) {
      return this.tree.find((node: Posizione) => {
        return node.posizioneId.split('/').length === 3 && sigla === node.sigla.toLowerCase();
      });
    }

    const pattern = new RegExp(`^${position.replace(/\//g, '\\/')}\\d+\\/$`, 'g');
    return this.tree.find((node: Posizione) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      return (pattern.exec(node.posizioneId)) && sigla === node.sigla.toLowerCase();
    });
  }

  private prepareToAddInFolder(nodeToModify: GestionePosizioniItem): void {
    if (this.controlDoubleInList(nodeToModify.posizioneId)) {
      this.warnDoubleMessage();
      return;
    }

    let newPos: string;
    const arrayFolderContent = this.tree.filter((node) => {
      if (this.nodeToModify() && node.posizioneId.startsWith(nodeToModify.posizioneId) && node.posizioneId.length === (nodeToModify.posizioneId.length + 2)) {
        return true;
      }
    });

    if (arrayFolderContent.length === 0) {
      newPos = '1';
    } else {
      let newPosNum: number;
      newPos = arrayFolderContent[arrayFolderContent.length - 1].posizioneId;
      newPos = newPos.replace(nodeToModify.posizioneId, '');
      newPos = newPos.slice(0, newPos.length - 1);
      newPosNum = Number(newPos);
      newPos = String(++newPosNum);
    }

    newPos = `${nodeToModify.posizioneId}${newPos}/`;
    const newFolder = this.getNewPosizione(newPos);

    this.posizioniService.postAddFolder(newFolder, nodeToModify.posizioneId).subscribe({
      next: resp => this.success(resp),
      error: err => this.trapError(err)
    });
  }

  private prepareToModify(nodeToModify: GestionePosizioniItem): void {
    if (this.folderForm.controls.name.dirty && this.controlDoubleInList(this.getParentPosition(nodeToModify.posizioneId))) {
      this.warnDoubleMessage();
      return;
    }

    this.applyFormValues(nodeToModify);
    this.posizioniService.putModifyFolder(nodeToModify).subscribe({
      next: () => this.success(),
      error: err => this.trapError(err)
    });
  }

  private prepareNewToSend(): void {
    if (this.folderForm.value.name === null || this.folderForm.value.name === '') {
      this.errorMessage();
      return;
    }

    if (this.controlDoubleInList(POSIZIONE_ROOT)) {
      this.warnDoubleMessage();
      return;
    }

    const onlyInter = this.tree.filter((doc) => {
      if (doc.posizioneId.split('/').length === 3) {
        return true;
      }
    });
    let newPos = onlyInter.length ? onlyInter[onlyInter.length - 1].posizioneId : '/0';
    const regex = /\//g;
    let newPosNum = Number(newPos.replace(regex, ''));
    newPos = `/${++newPosNum}/`;
    const newFolder = this.getNewPosizione(newPos);

    this.posizioniService.postAddFolder(newFolder, this.currentFather()).subscribe({
      next: resp => this.success(resp),
      error: err => this.trapError(err)
    });
  }

  private getNewPosizione(newPos: string): Posizione {
    return {
      chiuso: false,
      classificaId: this.folderForm.controls.classificaId.value,
      dataApertura: this.folderForm.value.dataApertura ?? new Date(),
      dataChiusura: this.folderForm.value.dataChiusura,
      dataScadenza: this.folderForm.value.dataScadenza,
      descrizione: this.folderForm.value.descrizione,
      note: this.folderForm.value.note,
      documento: [],
      documentoCopiaPer: [],
      paroleChiaveJson: this.serializeParoleChiave(),
      posizioneId: newPos,
      posizioneFisica: this.folderForm.controls.posizioneFisica.value || null,
      progressivo: this.folderForm.controls.progressivo.value,
      sigla: this.folderForm.getRawValue().name,
      tipologiaFascicolo: this.folderForm.controls.tipologiaFascicolo.value,
    };
  }

  private populateForm(nodeToModify: GestionePosizioniItem): void {
    this.folderForm.reset({
      name: nodeToModify.sigla,
      classificaId: nodeToModify.classificaId ?? null,
      descrizione: nodeToModify.descrizione ?? '',
      dataApertura: new Date(nodeToModify.dataApertura),
      dataChiusura: nodeToModify.dataChiusura ? new Date(nodeToModify.dataChiusura) : null,
      dataScadenza: nodeToModify.dataScadenza ? new Date(nodeToModify.dataScadenza) : null,
      note: nodeToModify.note ?? '',
      paroleChiave: this.parseParoleChiave(nodeToModify.paroleChiaveJson),
      posizioneFisica: nodeToModify.posizioneFisica ?? '',
      progressivo: nodeToModify.progressivo ?? null,
      tipologiaFascicolo: nodeToModify.tipologiaFascicolo ?? null,
    });
  }

  private getParentPosition(position: string) {
    const split = position.split('/');
    split.splice(split.length - 2, 1);
    return split.join('/');
  }

  private applyFormValues(posizione: GestionePosizioniItem): void {
    posizione.sigla = this.folderForm.getRawValue().name;
    posizione.classificaId = this.folderForm.controls.classificaId.value;
    posizione.descrizione = this.folderForm.value.descrizione;
    posizione.dataApertura = this.folderForm.getRawValue().dataApertura;
    posizione.dataChiusura = this.folderForm.value.dataChiusura;
    posizione.dataScadenza = this.folderForm.value.dataScadenza;
    posizione.note = this.folderForm.value.note;
    posizione.paroleChiaveJson = this.serializeParoleChiave();
    posizione.posizioneFisica = this.folderForm.controls.posizioneFisica.value || null;
    posizione.progressivo = this.folderForm.controls.progressivo.value;
    posizione.tipologiaFascicolo = this.folderForm.controls.tipologiaFascicolo.value;
  }

  private parseParoleChiave(rawJson: string | null | undefined): string[] {
    if (!rawJson) {
      return [];
    }

    try {
      const values = JSON.parse(rawJson) as unknown;
      if (!Array.isArray(values)) {
        return [];
      }

      return values
        .filter((value): value is string => typeof value === 'string')
        .map(value => value.trim())
        .filter(value => value.length > 0)
        .slice(0, 5);
    } catch {
      return [];
    }
  }

  private serializeParoleChiave(): string | null {
    const values = this.folderForm.controls.paroleChiave.getRawValue()
      .map(value => value?.trim())
      .filter((value): value is string => !!value)
      .slice(0, 5);

    return values.length > 0 ? JSON.stringify(values) : null;
  }

  private resetForm(): void {
    this.folderForm.reset({
      name: '',
      classificaId: null,
      descrizione: '',
      dataApertura: new Date(),
      dataChiusura: null,
      dataScadenza: null,
      note: '',
      paroleChiave: [],
      posizioneFisica: '',
      progressivo: null,
      tipologiaFascicolo: null,
    });
    this.folderForm.markAsPristine();
    this.folderForm.markAsUntouched();
  }

  private errorMessage(): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Errore',
      detail: 'Elemento non aggiunto'
    });
  }

  private successMessage(): void {
    this.messageService.add({
      severity: 'success',
      summary: 'Aggiunta',
      detail: 'Elemento aggiunto correttamente'
    });
  }

  private errorModifyMessage(): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Errore',
      detail: 'Elemento non modificato'
    });
  }

  private successModifyMessage(): void {
    this.messageService.add({
      severity: 'success',
      summary: 'Modificata',
      detail: 'Elemento modificato correttamente'
    });
  }

  private warnDoubleMessage(): void {
    this.messageService.add({
      severity: 'warn',
      summary: 'Elemento esistente',
      detail: 'Elemento già esistente'
    });
  }

  private success(result?: Posizione): void {
    this.isModify() ? this.successModifyMessage() : this.successMessage();
    this.isCompleted.emit(result ?? this.nodeToModify() ?? {} as Posizione);
    this.closeModalAddFolder();
  }

  private trapError(err: unknown): void {
    console.error(err);
    this.isModify() ? this.errorModifyMessage() : this.errorMessage();
  }

  private isModify(): boolean {
    return this.typeOfEvent() === 'mod';
  }
}
